/**
 * Ссылки на документ счёта: PDF и Word.
 *
 * На широком экране это обычные ссылки: PDF открывается в новой вкладке —
 * его смотрят, — Word скачивается, его правят.
 *
 * На телефоне открыть в новой вкладке нельзя. Установленное приложение новых
 * вкладок не создаёт вовсе, а мобильный Chrome не умеет показывать PDF внутри
 * себя: он всё равно скачает файл, но уже потеряв заданное имя и оставив
 * пользователя на пустой странице. Поэтому там оба формата забираются и
 * отдаются как загрузка — файл попадает в «Загрузки» под правильным именем
 * и открывается системной смотрелкой.
 */

import { useState } from 'react';
import { documentUrl, downloadFile } from '../api/client.js';
import { prefersDownload } from './isNarrow.js';

export function DocumentLinks({
  invoiceId,
  kind,
  label,
  onError,
}: {
  invoiceId: string;
  kind: 'schet' | 'akt';
  /** Подпись перед кнопками: «Счёт» или «Акт». */
  label: string;
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState<'pdf' | 'docx' | null>(null);

  const grab = async (format: 'pdf' | 'docx') => {
    setBusy(format);
    try {
      await downloadFile(documentUrl(invoiceId, kind, format), `${label}.${format}`);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Не удалось получить документ');
    } finally {
      setBusy(null);
    }
  };

  /*
    На телефоне группа тянется на всю отведённую ей ячейку, а форматы делят
    остаток поровну.

    Раньше ширина набиралась из содержимого, и на телефонах с увеличенным
    системным шрифтом «Счёт» и «Акт» переставали помещаться рядом и
    расползались на две строки — карточка счёта вырастала на строку там, где
    места хватало. Теперь ширину задаёт сетка снаружи, а не длина подписей.
  */
  const item =
    'flex-1 border-l px-1.5 py-1.5 text-center text-xs transition-colors hover:bg-black/5 disabled:opacity-50 md:flex-none md:px-2 md:py-2';
  const border = { borderColor: 'var(--otc-line)' };

  return (
    <span
      className="flex w-full items-center overflow-hidden rounded-lg border md:inline-flex md:w-auto"
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
    >
      <span className="whitespace-nowrap px-2 py-1.5 text-[13px] text-ink md:px-2.5 md:py-2 md:text-sm">
        {label}
      </span>

      {prefersDownload() ? (
        <button
          onClick={() => void grab('pdf')}
          disabled={busy !== null}
          className={`${item} text-ink2`}
          style={border}
        >
          {busy === 'pdf' ? '…' : 'PDF'}
        </button>
      ) : (
        <a
          href={documentUrl(invoiceId, kind, 'pdf')}
          target="_blank"
          rel="noreferrer"
          className={`${item} text-ink2`}
          style={border}
          title={`Открыть ${label.toLowerCase()} в PDF`}
        >
          PDF
        </a>
      )}

      {/* Word скачивается всегда: показывать его в браузере нечем. */}
      {prefersDownload() ? (
        <button
          onClick={() => void grab('docx')}
          disabled={busy !== null}
          className={`${item} text-ink2`}
          style={border}
        >
          {busy === 'docx' ? '…' : 'Word'}
        </button>
      ) : (
        <a
          href={documentUrl(invoiceId, kind, 'docx')}
          className={`${item} text-ink2`}
          style={border}
          title={`Скачать ${label.toLowerCase()} в Word`}
        >
          Word
        </a>
      )}
    </span>
  );
}
